import React, { useEffect, useRef, useState } from 'react'
import dynamic from 'next/dynamic'

// mobile mega menu should not be SSR, as burger still appears.
const MobileMegaMenu = dynamic(
    () => import('./components/MobileMegaMenu/MobileMegaMenu'),
    { ssr: false }
)

// Should be ssr to make sure that links are visible to users initially
const DesktopMegaMenu = dynamic(
    () => import('./components/DesktopMegaMenu/DesktopMegaMenu')
)

import cn from 'classnames'
import s from '@commonStyles/menus/MegaMenu.module.scss'
import { useNavMenu, useModal } from './hooks'
import { usePreloadedData } from 'preload'
import { useTranslation } from 'next-i18next/pages'
import { useStoreConfig } from 'config'
// El header sigue con el logo de la casa hasta 1250px. Ahí el hamburguesa
// no puede irse: el menú de categorías entra junto con el logo de desktop.
const DESKTOP_MENU_MIN = 1251
import { LoadingDots } from '@corratech/pylot-ui'
import type { CategoryTree } from '@pylot-data/pylotschema'

export const MegaMenu = ({
    className
}: {
    className: string
}): JSX.Element | null => {
    const [isOpen, setIsOpen] = useModal(false)
    const config = useStoreConfig()
    const variables = config.preload.menu?.variables
    const preloadedMenuData = usePreloadedData('menu')

    // Hasta 1250px: burger. Desde ahí, el menú de categorías de desktop.
    const [isCompact, setIsCompact] = useState(false)

    useEffect(() => {
        const update = () => {
            const compact = window.innerWidth < DESKTOP_MENU_MIN
            setIsCompact(compact)
            if (!compact) setIsOpen(false)
        }
        update()
        window.addEventListener('resize', update)
        return () => window.removeEventListener('resize', update)
    }, [])

    const isDev = process.env.NODE_ENV === 'development'
    const {
        navigation,
        error,
        isValidating
    } = useNavMenu(
        { ...variables, onlyTopLevel: false },
        {
            initialData: isDev ? undefined : preloadedMenuData,
            revalidateOnMount: true,
            revalidateOnFocus: isDev,
            keepPreviousData: !isDev
        }
    )

    const lastNavigationRef = useRef<CategoryTree | null>(null)
    if (navigation) {
        lastNavigationRef.current = navigation
    }
    const navigationData = navigation ?? lastNavigationRef.current

    const { t } = useTranslation('common')

    if (!variables?.category_uid) {
        return null
    }

    if (error && !navigationData) {
        return <span>{t('Failed to load navigation menu')}</span>
    }

    if (!navigationData) {
        if (isValidating) {
            return (
                <div
                    className={cn(className, s.megaMenuCms)}
                    aria-busy="true"
                >
                    <LoadingDots />
                </div>
            )
        }
        return null
    }

    return (
        <div className={cn(className, s.megaMenuCms)}>
            {isCompact ? (
                <>
                    <div
                        className={cn([s.burgerHolder], {
                            [s.activeBurger]: isOpen
                        })}
                        onClick={() => setIsOpen(!isOpen)}
                        aria-hidden="true"
                    >
                        <svg
                            className={s.burgerIcon}
                            width="50"
                            height="42"
                            viewBox="0 0 50 42"
                            fill="none"
                            aria-hidden
                        >
                            <path
                                className="burger-bars"
                                d="M37.648 12.87C37.648 12.0802 37.0078 11.44 36.218 11.44H13.9244C13.1346 11.44 12.4944 12.0802 12.4944 12.87C12.4944 13.6598 13.1346 14.3 13.9244 14.3H36.218C37.0078 14.3 37.648 13.6598 37.648 12.87Z"
                                fill="#534E46"
                            />
                            <path
                                className="burger-bars"
                                d="M37.6336 20.4967C37.6336 19.7069 36.9934 19.0667 36.2036 19.0667H13.91C13.1202 19.0667 12.48 19.7069 12.48 20.4967C12.48 21.2864 13.1202 21.9267 13.91 21.9267H36.2036C36.9934 21.9267 37.6336 21.2864 37.6336 20.4967Z"
                                fill="#534E46"
                            />
                            <path
                                className="burger-bars"
                                d="M37.6336 28.1233C37.6336 27.3336 36.9934 26.6933 36.2036 26.6933H13.91C13.1202 26.6933 12.48 27.3336 12.48 28.1233C12.48 28.9131 13.1202 29.5533 13.91 29.5533H36.2036C36.9934 29.5533 37.6336 28.9131 37.6336 28.1233Z"
                                fill="#534E46"
                            />
                        </svg>
                    </div>
                    <MobileMegaMenu
                        isOpen={isOpen}
                        setIsOpen={setIsOpen}
                        navData={navigationData}
                    />
                </>
            ) : (
                <DesktopMegaMenu navData={navigationData} />
            )}
        </div>
    )
}
